import { forwardRef, type KeyboardEvent } from 'react';

import { ChevronDown } from '../../icons/lucide/ChevronDown';
import { Platform } from '../../platform';
import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { MarqueeLabel } from '../MarqueeLabel';
import {
  CHEVRON_BUTTON_PX,
  CHEVRON_ICON_SIZE_FOR,
  LEFT_ICON_SIZE_FOR,
  resolveSidebarMenuItemIconColor,
} from './SidebarMenuItem.icons';
import type { SidebarMenuItemProps } from './SidebarMenuItem.types';
import {
  sidebarMenuItemChevronVariants,
  sidebarMenuItemDropCueClass,
  sidebarMenuItemTextVariants,
  sidebarMenuItemVariants,
} from './SidebarMenuItem.variants';

const IS_WEB = Platform.OS === 'web';

// Detached ref shape — same rationale as Button.tsx / Pressable.tsx: pnpm
// hoists multiple @types/react copies so `Ref<T>` becomes nominally distinct
// across consumer apps even when versions match.
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

// Tree indentation: every nesting level adds 24px, applied OUTSIDE the row
// background so hover/active fills start at the indented edge. `depth={0}` is
// the top of the tree and stays flush — a consumer that renders the tree under
// a section header pads the whole tree instead, so the header's own row is not
// dragged along (see the sidebar's Personal Library / Study Groups sections).
//
// A row WITHOUT a chevron also reserves the chevron button's exact footprint,
// so a deck's left icon lines up with a foldable sibling folder's. Nothing is
// added on top of it: Figma butts the content frame straight against the
// button (the chevron cancels the row's `gap-2` with `-mr-2`).
//
// "Inside a tree" is `depth` being passed at all, not `depth > 0`: plain nav
// rows (Home, Quizzes, …) omit it and stay flush left, while a top-level tree
// row passes `depth={0}` and still lines up with its foldable siblings.
const INDENT_PER_LEVEL = 24;

/**
 * Cross-platform sidebar menu item — the founding block for both plain
 * sidebar rows (Home, Quizzes, …) and the foldable Personal Library / Study
 * Groups trees. Renders identically on web and React Native.
 *
 * Mirrors Figma "Flashka Design System" file `YZK9r3wDhvw1pY60KlVEIj`,
 * component set "Side bar menu item updated" (light `5311:5347`, dark
 * `5326:39356`):
 *
 *   - XL (56px) · L (48px) · M (44px) · S (32px)
 *   - optional leading 36px chevron button for foldable rows
 *   - optional left slot (icon / avatar / emoji)
 *   - marquee label (fills remaining width — right content is ALWAYS at the
 *     right end)
 *   - optional right slot(s): always-visible (`rightSlot`) and hover-revealed
 *     (`hoverRightSlot`)
 *
 * @example
 * ```tsx
 * import { SidebarMenuItem } from '@flashka/shared/ui';
 * import { House, Globe, Ellipsis } from '@flashka/shared/ui/icons';
 *
 * // Plain nav row
 * <SidebarMenuItem leftIcon={House} active={route === '/'} onPress={goHome}>
 *   Home
 * </SidebarMenuItem>
 *
 * // Foldable folder with a hover-only kebab
 * <SidebarMenuItem
 *   leftIcon={FolderOpen}
 *   expanded={open}
 *   onToggleExpand={() => setOpen((v) => !v)}
 *   rightSlot={<Globe size={20} color="currentColor" />}
 *   hoverRightSlot={<FolderKebabMenu />}
 *   onPress={openFolder}>
 *   New Folder
 * </SidebarMenuItem>
 *
 * // Nested leaf (deck inside a folder) — aligns under the parent's label
 * <SidebarMenuItem depth={1} leftSlot={<DeckEmoji />} onPress={openDeck}>
 *   Deck 1
 * </SidebarMenuItem>
 * ```
 */
function SidebarMenuItemInner(
  {
    children,
    onPress,
    active = false,
    highlight,
    dropTarget = false,
    disabled = false,
    fullWidth = true,
    depth,
    expanded = false,
    onToggleExpand,
    toggleAccessibilityLabel,
    leftIcon: LeftIcon,
    leftSlot,
    rightSlot,
    hoverRightSlot,
    hoverRightSlotVisible = false,
    iconColor: iconColorOverride,
    size,
    className,
    textClassName,
    as,
    accessibilityLabel,
    testID,
    webProps,
    nativeProps,
    // Slot-style consumers (Radix triggers, dnd-kit, react-router Link
    // wrappers) clone the row and inject extra props at the top level —
    // funnel them to the Pressable, same pattern as Button / MenuListItem.
    ...injected
  }: SidebarMenuItemProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const { isDark } = useTheme();
  const resolvedSize = size ?? 'm';
  // A drop target GROWS on web (`sidebarMenuItemDropCueClass`) and is tinted
  // only where it cannot grow.
  const resolvedHighlight = dropTarget && !IS_WEB ? 'primary' : (highlight ?? 'none');
  const hasChevron = onToggleExpand != null;

  const textClass = cn(
    sidebarMenuItemTextVariants({
      size: resolvedSize,
      active,
      highlight: resolvedHighlight,
    }),
    textClassName,
  );

  // Paint the label colour on the container so SVG icons inherit it via CSS
  // `currentColor` on web (same trick as Button / MenuListItem).
  const containerClass = cn(
    sidebarMenuItemVariants({
      size: resolvedSize,
      active,
      highlight: resolvedHighlight,
      disabled,
      fullWidth,
    }),
    textClass,
    sidebarMenuItemDropCueClass(dropTarget),
    className,
  );

  const iconColor =
    iconColorOverride ??
    (IS_WEB
      ? 'currentColor'
      : resolveSidebarMenuItemIconColor(active, resolvedHighlight, isDark));

  // Chevron press must NOT bubble into the row's own onPress.
  const handleToggle = (e: PressEvent) => {
    e.stopPropagation();
    onToggleExpand?.(e);
  };

  // NO `accessibilityRole` here, deliberately. On iOS the row above is itself
  // an accessibility element (RN's Pressable defaults to `accessible`), which
  // merges this chevron into it — measured on iPad Pro 13-inch (M5),
  // 2026-09-08: the rail's "Personal Library" header row reports as ONE node
  // with no children at all, so a trait here would be unobservable. Harmless
  // for a SECTION header, whose own `onPress` is the same toggle; a real gap
  // for a FOLDER row, where `onPress` opens the folder and only the chevron
  // expands it in place, so a screen-reader user cannot expand one. Fixing
  // that needs the row un-merged or a custom accessibility action (the
  // `cardActionsA11y` pattern) — a product call, not a prop.
  const chevron = hasChevron ? (
    <Pressable
      className={sidebarMenuItemChevronVariants({ size: resolvedSize })}
      disabled={disabled}
      onPress={handleToggle}
      accessibilityLabel={toggleAccessibilityLabel}
      testID={subTestID(testID, 'toggle')}
      webProps={{ 'aria-expanded': expanded }}>
      <View
        className={cn(
          'web:transition-transform web:duration-150 web:ease-out',
          !expanded && 'web:-rotate-90',
        )}
        // Native rotates via a plain style, NOT a class — same rule as
        // SectionHeader: toggling a NativeWind transform class re-parses the
        // className at render time, which races react-navigation's context on
        // Fabric and takes the screen down with a bogus "Couldn't find a
        // navigation context" error when the row sits inside a FlashList.
        nativeProps={
          !IS_WEB && !expanded
            ? { style: { transform: [{ rotate: '-90deg' }] } }
            : undefined
        }>
        <ChevronDown color={iconColor} size={CHEVRON_ICON_SIZE_FOR[resolvedSize]} />
      </View>
    </Pressable>
  ) : null;

  // The label always owns the leftover width (`flex-1`) so right-side content
  // sits at the right end of the row — Figma state 5311:6524 shows the slot
  // glued to the label, but that state is a known mistake in the design file.
  //
  // `playOnce={active}`: the row the user just opened plays ONE delayed
  // there-and-back pass of its truncated label, then rests — never an
  // unattended loop (see MarqueeLabel.playOnce).
  const labelNode =
    typeof children === 'string' ? (
      <MarqueeLabel
        label={children}
        playOnce={active}
        className={cn('min-w-0 flex-1', textClass)}
      />
    ) : (
      <View className="min-w-0 flex-1 flex-row items-center">{children}</View>
    );

  // Hover-revealed trailing content: web hides it until the row is hovered or
  // holds keyboard focus; an active row keeps it visible (per the Figma
  // sidebar example); native (no hover) always shows it.
  //
  // Keyboard focus is `focus-visible`, not `focus-within`: a row with
  // interactive children renders as a focusable div, so a plain CLICK focused
  // it and `focus-within` pinned the slot open until you clicked elsewhere.
  // Two selectors because the slot must survive focus moving INTO it: the row
  // itself (`group-focus-visible`) or any descendant (`group-has-`).
  //
  // `hoverRightSlotVisible` pins it open regardless. A menu opened FROM this
  // slot portals its content outside the row, so neither `hover` nor
  // `focus-within` still holds once the pointer moves onto the menu — the
  // trigger would vanish out from under the very menu it opened. Callers pass
  // their menu's open state here.
  const hoverSlot =
    hoverRightSlot != null ? (
      <View
        className={cn(
          'shrink-0 flex-row items-center',
          IS_WEB && !active && !hoverRightSlotVisible
            ? 'hidden group-hover:flex group-focus-visible:flex group-has-[:focus-visible]:flex'
            : 'flex',
        )}>
        {hoverRightSlot}
      </View>
    ) : null;

  // Never nest <button> in <button> on web: rows carrying interactive
  // children render as a div (Pressable adds role="button" + tabIndex), with
  // Enter/Space activation restored below.
  const hasInteractiveChildren =
    hasChevron || rightSlot != null || hoverRightSlot != null;
  const resolvedAs = as ?? (hasInteractiveChildren ? 'div' : 'button');

  let mergedWebProps = webProps;
  if (IS_WEB && resolvedAs === 'div' && onPress && !disabled) {
    const callerKeyDown = (
      webProps as { onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void }
    )?.onKeyDown;
    mergedWebProps = {
      ...webProps,
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        callerKeyDown?.(e);
        if (e.defaultPrevented || e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress(e as unknown as PressEvent);
        }
      },
    };
  }

  // The row's BUTTON semantics — NATIVE ONLY, and that asymmetry is the two
  // platforms' accessibility models, not a fork.
  //
  // Web takes them from the ELEMENT: `resolvedAs` is a real `<button>`, or a
  // `<div>` that Pressable already gives `role="button"` plus Enter/Space
  // activation. Passing `accessibilityRole` there would put a redundant
  // `role="button"` on an element that carries the role natively, so it goes
  // through `nativeProps` — which the web Pressable discards outright — rather
  // than the top-level prop.
  //
  // Native has no element to carry it and RN's Pressable adds no trait of its
  // own, so every sidebar row arrived in the iOS accessibility tree as a plain
  // `Other` node: VoiceOver read the label and never said "button", and no role
  // selector could separate a row from the container Views that share its
  // label. Measured on iPad Pro 13-inch (M5), 2026-09-08 — every rail row
  // (three nav rows, both section headers, 15 deck rows, one group row) was
  // `Other`, while the DS `DeckCard` tiles on the Home canvas behind them,
  // which do pass a role, were `Button`.
  //
  // Gated on `onPress`: a row nothing happens on is not a button. Caller
  // `nativeProps` spread LAST so a row that needs a different trait
  // (`menuitem` inside a menu, `tab`) can still say so.
  const rowNativeProps =
    onPress != null
      ? { accessibilityRole: 'button' as const, ...nativeProps }
      : nativeProps;

  const row = (
    <Pressable
      {...injected}
      ref={ref}
      as={resolvedAs}
      className={containerClass}
      disabled={disabled}
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={mergedWebProps}
      nativeProps={rowNativeProps}>
      {chevron}
      {LeftIcon ? (
        <LeftIcon color={iconColor} size={LEFT_ICON_SIZE_FOR[resolvedSize]} />
      ) : leftSlot != null ? (
        leftSlot
      ) : null}
      {labelNode}
      {rightSlot}
      {hoverSlot}
    </Pressable>
  );

  const indent =
    depth == null
      ? 0
      : depth * INDENT_PER_LEVEL + (hasChevron ? 0 : CHEVRON_BUTTON_PX[resolvedSize]);
  if (indent === 0) return row;
  return (
    <View
      // `shrink-0` for the same reason as the row itself — see
      // `sidebarMenuItemVariants`; here the wrapper is the flex child.
      className={cn('shrink-0 flex-row', fullWidth && 'w-full')}
      webProps={{ style: { paddingLeft: indent } }}
      nativeProps={{ style: { paddingLeft: indent } }}>
      {row}
    </View>
  );
}

// Same explicit forwardRef cast pattern as Button / MenuListItem — see
// Button.tsx for the multi-copy `@types/react` rationale.
export const SidebarMenuItem = forwardRef(SidebarMenuItemInner as never) as unknown as (
  props: SidebarMenuItemProps & { ref?: AnyRef<HTMLElement> },
) => ReturnType<typeof SidebarMenuItemInner>;

export type { PressEvent };
